import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, update, remove } from "firebase/database";
import { getAuth } from 'firebase/auth';
import DashboardHeader from '../../AdminComponents/DashboardHeader';
import GenerativeOrbAnimation from '../../Pages/GenerativeOrbAnimation';
import ProductCreator from '../Components/ProductCreator';
import CustomerFormBuilder from '../Components/CustomerFormBuilder';
import ProductCustomers from '../Components/ProductCustomers';
import TriggerBuilder from '../Components/TriggerBuilder';
import './ProductSettings.css';

const ProductSettings = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [showEditModal, setShowEditModal] = useState(false);
    const [salesSettings, setSalesSettings] = useState(null);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Load product data
    useEffect(() => {
        if (currentUser && productId) {
            const productRef = ref(db, `products/${productId}`);
            get(productRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const productData = snapshot.val();
                    if (productData.providerId === currentUser.uid) {
                        setProduct({ id: productId, ...productData });
                    } else {
                        alert('You do not have permission to edit this product.');
                        navigate('/sales');
                    }
                } else {
                    alert('Product not found.');
                    navigate('/sales');
                }
                setLoading(false);
            });

            // Load user's sales settings and stripe settings
            const userRef = ref(db, `Users/${currentUser.uid}`);
            get(userRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const userData = snapshot.val();
                    // Combine salesSettings with stripeSettings
                    const combinedSettings = {
                        ...userData.salesSettings,
                        ...userData.stripeSettings, // Include stripe settings
                        stripeConfigured: !!(userData.stripeSettings?.stripePublishableKey && userData.stripeSettings?.stripeSecretKey),
                        webhookConfigured: !!userData.stripeSettings?.stripeWebhookSecret
                    };
                    setSalesSettings(combinedSettings);
                    console.log('🔧 ProductSettings - Loaded user settings:', combinedSettings);
                }
            });
        }
    }, [currentUser, productId, db, navigate]);

    const handleSaveProduct = async (updatedData) => {
        setSaving(true);
        try {
            const productRef = ref(db, `products/${productId}`);
            await update(productRef, updatedData);
            setProduct(prev => ({ ...prev, ...updatedData }));
            
            // Only show alert for manual saves (like edit modal)
            if (updatedData.title || updatedData.description) {
                setShowEditModal(false);
                alert('Product updated successfully!');
            }
        } catch (error) {
            console.error('Error updating product:', error);
            alert('Error updating product. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteProduct = async () => {
        if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
            try {
                const productRef = ref(db, `products/${productId}`);
                await remove(productRef);
                alert('Product deleted successfully.');
                navigate('/sales');
            } catch (error) {
                console.error('Error deleting product:', error);
                alert('Error deleting product. Please try again.');
            }
        }
    };

    const handlePublishToggle = async () => {
        const newStatus = product.status === 'published' ? 'draft' : 'published';
        try {
            const productRef = ref(db, `products/${productId}`);
            await update(productRef, { status: newStatus });
            setProduct(prev => ({ ...prev, status: newStatus }));
        } catch (error) {
            console.error('Error updating product status:', error);
            alert('Error updating product status. Please try again.');
        }
    };

    const getProductTypeInfo = (type) => {
        switch (type) {
            case 'digital':
                return { label: 'Digital Product', icon: 'fas fa-download', color: '#6366f1' };
            case 'course':
                return { label: 'Online Course', icon: 'fas fa-graduation-cap', color: '#10b981' };
            case 'event':
                return { label: 'Live Event', icon: 'fas fa-calendar-alt', color: '#f59e0b' };
            default:
                return { label: 'Product', icon: 'fas fa-box', color: '#6b7280' };
        }
    };

    if (loading) {
        return (
            <div className="loading-spinner">
                <h1>BonusClub</h1>
                <div className="spinner-circle"></div>
            </div>
        );
    }

    if (!product) {
        return (
            <div>
                <DashboardHeader />
                <div className="sales-error-container">
                    <h1>Product not found</h1>
                    <button onClick={() => navigate('/sales')}>Back to Dashboard</button>
                </div>
            </div>
        );
    }

    const typeInfo = getProductTypeInfo(product.type);

    return (
        <div>
            <DashboardHeader />
            <GenerativeOrbAnimation />
            
            <div className="sales-dashboard-container">
                <div className="sally-product-settings-container">
                    {/* Sally Product Settings Header */}
                    <div className="sally-product-settings-header">
                        <div className="sally-product-settings-info">
                            <div className="sally-product-settings-type-badge" style={{ '--type-color': typeInfo.color }}>
                                <i className={`${typeInfo.icon} sally-product-settings-type-icon`}></i>
                                <span>{typeInfo.label}</span>
                            </div>
                            <h1>{product.title}</h1>
                            <p>{product.description}</p>
                        </div>
                        
                        <div className="sally-product-settings-actions">
                            <button 
                                className={`sally-product-settings-status-btn ${product.status === 'published' ? 'published' : 'draft'}`}
                                onClick={handlePublishToggle}
                            >
                                {product.status === 'published' ? (
                                    <>
                                        <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: '6px' }}></i>
                                        Published
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-edit" style={{ color: '#f59e0b', marginRight: '6px' }}></i>
                                        Draft
                                    </>
                                )}
                            </button>
                            <button 
                                className="sally-product-settings-edit-btn"
                                onClick={() => setShowEditModal(true)}
                            >
                                ✏️ Edit Product
                            </button>
                            <button 
                                className="sally-product-settings-delete-btn"
                                onClick={handleDeleteProduct}
                            >
                                🗑️ Delete
                            </button>
                        </div>
                    </div>

                    {/* New Layout: Sidebar + Content */}
                    <div className="sally-settings-layout">
                        {/* Left Sidebar Navigation */}
                        <div className="sally-settings-sidebar">
                            <div className="sally-sidebar-header">
                                <h3>Settings</h3>
                                <p>Configure your product</p>
                            </div>
                            
                            <nav className="sally-sidebar-nav">
                                <button 
                                    className={`sally-sidebar-tab ${activeTab === 'general' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('general')}
                                >
                                    <i className="fas fa-cog sally-sidebar-icon"></i>
                                    <span>General Settings</span>
                                </button>
                                <button 
                                    className={`sally-sidebar-tab ${activeTab === 'form' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('form')}
                                >
                                    <i className="fas fa-clipboard-list sally-sidebar-icon"></i>
                                    <span>Customer Form</span>
                                </button>
                                <button 
                                    className={`sally-sidebar-tab ${activeTab === 'customers' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('customers')}
                                >
                                    <i className="fas fa-users sally-sidebar-icon"></i>
                                    <span>Customers</span>
                                </button>
                                <button 
                                    className={`sally-sidebar-tab ${activeTab === 'triggers' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('triggers')}
                                >
                                    <i className="fas fa-bolt sally-sidebar-icon"></i>
                                    <span>Triggers</span>
                                </button>
                            </nav>
                        </div>

                        {/* Right Content Area */}
                        <div className="sally-content-area">
                        {activeTab === 'general' && (
                            <div className="sally-general-settings">
                                {/* Fresh Product Information Cards */}
                                <div className="sally-fresh-section">
                                    <div className="sally-fresh-header">
                                        <div className="sally-fresh-icon">
                                            <i className="fas fa-info-circle"></i>
                                        </div>
                                        <div className="sally-fresh-title">
                                            <h3>Product Information</h3>
                                            <p>Essential details about your product</p>
                                        </div>
                                    </div>
                                    
                                    <div className="sally-fresh-cards">
                                        <div className="sally-fresh-card">
                                            <div className="sally-fresh-card-icon">
                                                <i className="fas fa-fingerprint"></i>
                                            </div>
                                            <div className="sally-fresh-card-content">
                                                <label>Product ID</label>
                                                <span className="sally-fresh-value">{productId}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="sally-fresh-card">
                                            <div className="sally-fresh-card-icon">
                                                <i className="fas fa-calendar-plus"></i>
                                            </div>
                                            <div className="sally-fresh-card-content">
                                                <label>Created</label>
                                                <span className="sally-fresh-value">
                                                    {product.createdAt ? new Date(product.createdAt).toLocaleDateString() : 'Unknown'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="sally-fresh-card">
                                            <div className="sally-fresh-card-icon">
                                                <i className={`fas ${product.status === 'published' ? 'fa-check-circle' : 'fa-edit'}`}></i>
                                            </div>
                                            <div className="sally-fresh-card-content">
                                                <label>Status</label>
                                                <span className={`sally-fresh-status ${product.status}`}>
                                                    {product.status}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="sally-fresh-card">
                                            <div className="sally-fresh-card-icon">
                                                <i className={typeInfo.icon}></i>
                                            </div>
                                            <div className="sally-fresh-card-content">
                                                <label>Type</label>
                                                <span className="sally-fresh-value">{typeInfo.label}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Fresh Content Overview */}
                                <div className="sally-fresh-section">
                                    <div className="sally-fresh-header">
                                        <div className="sally-fresh-icon">
                                            <i className="fas fa-folder-open"></i>
                                        </div>
                                        <div className="sally-fresh-title">
                                            <h3>Content Overview</h3>
                                            <p>Manage your product's content and assets</p>
                                        </div>
                                    </div>
                                    
                                    {product.type === 'digital' && (
                                        <div className="sally-fresh-content-grid">
                                            {product.contentData?.uploadedFiles?.length > 0 && (
                                                <div className="sally-fresh-content-card">
                                                    <div className="sally-fresh-content-header">
                                                        <div className="sally-fresh-content-icon files">
                                                            <i className="fas fa-file-download"></i>
                                                        </div>
                                                        <h4>Files & Downloads</h4>
                                                    </div>
                                                    <div className="sally-fresh-files-list">
                                                        {product.contentData.uploadedFiles.map((file, index) => (
                                                            <div key={index} className="sally-fresh-file-item">
                                                                <i className="fas fa-file-pdf"></i>
                                                                <span>{file.name}</span>
                                                                <div className="sally-fresh-file-badge">PDF</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {product.contentData?.downloadUrls?.length > 0 && (
                                                <div className="sally-fresh-content-card">
                                                    <div className="sally-fresh-content-header">
                                                        <div className="sally-fresh-content-icon links">
                                                            <i className="fas fa-external-link-alt"></i>
                                                        </div>
                                                        <h4>External Links</h4>
                                                    </div>
                                                    <div className="sally-fresh-links-list">
                                                        {product.contentData.downloadUrls.map((url, index) => (
                                                            <div key={index} className="sally-fresh-link-item">
                                                                <i className="fas fa-link"></i>
                                                                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                                                <div className="sally-fresh-link-badge">LINK</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {product.type === 'course' && (
                                        <div className="sally-fresh-content-grid">
                                            <div className="sally-fresh-content-card">
                                                <div className="sally-fresh-content-header">
                                                    <div className="sally-fresh-content-icon course">
                                                        <i className="fas fa-graduation-cap"></i>
                                                    </div>
                                                    <h4>Course Structure</h4>
                                                </div>
                                                <div className="sally-fresh-modules-list">
                                                    {product.contentData?.videoModules?.map((module, index) => (
                                                        <div key={index} className="sally-fresh-module-item">
                                                            <div className="sally-fresh-module-icon">
                                                                <i className="fas fa-play-circle"></i>
                                                            </div>
                                                            <div className="sally-fresh-module-content">
                                                                <div className="sally-fresh-module-title">
                                                                    Module {index + 1}: {module.title}
                                                                </div>
                                                                {module.duration && (
                                                                    <div className="sally-fresh-module-duration">
                                                                        <i className="fas fa-clock"></i>
                                                                        {module.duration}
                                                                    </div>
                                                                )}
                                                                {module.documents?.length > 0 && (
                                                                    <div className="sally-fresh-module-docs">
                                                                        <i className="fas fa-paperclip"></i>
                                                                        {module.documents.length} document(s)
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="sally-fresh-module-badge">
                                                                Module {index + 1}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {product.type === 'event' && (
                                        <div className="sales-content-preview">
                                            <h4>Event Details</h4>
                                            <div className="sales-event-preview">
                                                <div className="sales-event-type">
                                                    {product.contentData?.eventType === 'digital' ? (
                                                    <>
                                                        <i className="fas fa-laptop" style={{ marginRight: '6px' }}></i>
                                                        Digital Event
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-map-marker-alt" style={{ marginRight: '6px' }}></i>
                                                        In-Person Event
                                                    </>
                                                )}
                                                </div>
                                                {product.contentData?.eventDays?.length > 0 && (
                                                    <div className="sales-days-preview">
                                                        <strong>{product.contentData.eventDays.length} Event Day(s)</strong>
                                                    </div>
                                                )}
                                                {product.contentData?.tickets?.length > 0 && (
                                                    <div className="sales-tickets-preview">
                                                        <strong>{product.contentData.tickets.length} Ticket Type(s)</strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Payment Settings Section */}
                                <div className="sally-fresh-section">
                                    <div className="sally-fresh-header">
                                        <div className="sally-fresh-icon">
                                            <i className="fas fa-credit-card"></i>
                                        </div>
                                        <div className="sally-fresh-title">
                                            <h3>Payment Configuration</h3>
                                            <p>Configure how customers can purchase this product</p>
                                        </div>
                                    </div>
                                    
                                    <div className="sally-payment-settings">
                                        {/* Current Price Display */}
                                        <div className="sally-payment-info-card">
                                            <div className="sally-payment-info-header">
                                                <div className="sally-payment-info-icon">
                                                    <i className="fas fa-tag"></i>
                                                </div>
                                                <div className="sally-payment-info-content">
                                                    <label>Current Price</label>
                                                    <span className="sally-payment-price">
                                                        {product.price === 0 || product.price === '0' ? 
                                                            'FREE' : 
                                                            `€${product.price || 'Not set'}`
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Payment Method Configuration */}
                                        <div className="sally-payment-methods">
                                            <h4>Payment Method</h4>
                                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                                                Choose how customers will purchase this product
                                            </p>
                                            
                                            {/* Payment Method Radio Selection */}
                                            <div className="sally-payment-method-selector">
                                                {/* Stripe Payment Option */}
                                                <div className={`sally-payment-option ${(product.paymentMethod || 'stripe') === 'stripe' ? 'active' : ''}`}>
                                                    <label className="sally-payment-option-label">
                                                        <input 
                                                            type="radio" 
                                                            name="paymentMethod"
                                                            value="stripe"
                                                            checked={(product.paymentMethod || 'stripe') === 'stripe'}
                                                            onChange={async (e) => {
                                                                await handleSaveProduct({ 
                                                                    paymentMethod: 'stripe',
                                                                    enableStripePayment: true,
                                                                    enableFormOnly: false
                                                                });
                                                            }}
                                                            className="sally-payment-radio"
                                                        />
                                                        <div className="sally-payment-option-content">
                                                            <div className="sally-payment-option-header">
                                                                <div className="sally-payment-method-icon stripe">
                                                                    <img src="/img/stripe.svg" alt="Stripe" />
                                                                </div>
                                                                <div className="sally-payment-method-info">
                                                                    <h5>Stripe Online Payment</h5>
                                                                    <p>Secure online payment processing</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </label>
                                                </div>

                                                {/* Form Registration Option */}
                                                <div className={`sally-payment-option ${product.paymentMethod === 'form' ? 'active' : ''}`}>
                                                    <label className="sally-payment-option-label">
                                                        <input 
                                                            type="radio" 
                                                            name="paymentMethod"
                                                            value="form"
                                                            checked={product.paymentMethod === 'form'}
                                                            onChange={async (e) => {
                                                                await handleSaveProduct({ 
                                                                    paymentMethod: 'form',
                                                                    enableStripePayment: false,
                                                                    enableFormOnly: true
                                                                });
                                                            }}
                                                            className="sally-payment-radio"
                                                        />
                                                        <div className="sally-payment-option-content">
                                                            <div className="sally-payment-option-header">
                                                                <div className="sally-payment-method-icon form">
                                                                    <i className="fas fa-clipboard-list"></i>
                                                                </div>
                                                                <div className="sally-payment-method-info">
                                                                    <h5>Form Registration Only</h5>
                                                                    <p>Collect customer info without payment</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            {/* Stripe Configuration (only when Stripe is selected) */}
                                            {(product.paymentMethod || 'stripe') === 'stripe' && (
                                                <div className="sally-stripe-config">
                                                    <div className="sally-stripe-fields">
                                                        <div className="sally-stripe-field">
                                                            <label>Stripe Product ID</label>
                                                            <input 
                                                                type="text"
                                                                value={product.stripeProductId || ''}
                                                                onChange={(e) => {
                                                                    setProduct(prev => ({ ...prev, stripeProductId: e.target.value }));
                                                                }}
                                                                onBlur={async (e) => {
                                                                    await handleSaveProduct({ stripeProductId: e.target.value });
                                                                }}
                                                                placeholder="prod_ABC123..."
                                                                className="sally-stripe-input"
                                                            />
                                                        </div>
                                                        <div className="sally-stripe-field">
                                                            <label>Stripe Price ID</label>
                                                            <input 
                                                                type="text"
                                                                value={product.stripePriceId || ''}
                                                                onChange={(e) => {
                                                                    setProduct(prev => ({ ...prev, stripePriceId: e.target.value }));
                                                                }}
                                                                onBlur={async (e) => {
                                                                    await handleSaveProduct({ stripePriceId: e.target.value });
                                                                }}
                                                                placeholder="price_XYZ789..."
                                                                className="sally-stripe-input"
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    {(!product.stripeProductId || !product.stripePriceId) && (
                                                        <div className="sally-stripe-warning">
                                                            <i className="fas fa-exclamation-triangle"></i>
                                                            <span>Configure both Product ID and Price ID for Stripe payments to work</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Success/Cancel URLs */}
                                            <div className="sally-payment-urls">
                                                <h4>Redirect URLs</h4>
                                                <div className="sally-url-fields">
                                                    <div className="sally-url-field">
                                                        <label>Success URL</label>
                                                        <input 
                                                            type="url"
                                                            value={product.successUrl || ''}
                                                            onChange={(e) => {
                                                                setProduct(prev => ({ ...prev, successUrl: e.target.value }));
                                                            }}
                                                            onBlur={async (e) => {
                                                                await handleSaveProduct({ successUrl: e.target.value });
                                                            }}
                                                            placeholder="https://yoursite.com/success"
                                                            className="sally-url-input"
                                                        />
                                                    </div>
                                                    <div className="sally-url-field">
                                                        <label>Cancel URL</label>
                                                        <input 
                                                            type="url"
                                                            value={product.cancelUrl || ''}
                                                            onChange={(e) => {
                                                                setProduct(prev => ({ ...prev, cancelUrl: e.target.value }));
                                                            }}
                                                            onBlur={async (e) => {
                                                                await handleSaveProduct({ cancelUrl: e.target.value });
                                                            }}
                                                            placeholder="https://yoursite.com/cancel"
                                                            className="sally-url-input"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'form' && (
                            <CustomerFormBuilder 
                                product={product}
                                onSave={handleSaveProduct}
                            />
                        )}

                        {activeTab === 'customers' && (
                            <ProductCustomers 
                                product={product}
                            />
                        )}

                        {activeTab === 'triggers' && (
                            <TriggerBuilder 
                                product={product}
                                onSave={handleSaveProduct}
                            />
                        )}
                        </div>
                    </div>
                </div>

                {showEditModal && (
                    <ProductCreator 
                        onClose={() => {
                            setShowEditModal(false);
                            // Force refresh of all ProductInfoWidgets
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('productUpdated', {
                                    detail: { 
                                        productId: product.id,
                                        action: 'modalClosed',
                                        timestamp: new Date().toISOString()
                                    }
                                }));
                                console.log('🔄 ProductSettings - Forced refresh after modal close');
                            }, 100);
                        }}
                        onProductCreated={(productId) => {
                            setShowEditModal(false);
                            // Reload product data
                            const productRef = ref(db, `products/${productId}`);
                            get(productRef).then((snapshot) => {
                                if (snapshot.exists()) {
                                    setProduct({ id: productId, ...snapshot.val() });
                                }
                            });
                        }}
                        salesSettings={salesSettings}
                        preselectedType={product.type}
                        editMode={true}
                        existingProduct={product}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductSettings;
